import { css, useTheme } from '@emotion/react';
import * as Dialog from '@radix-ui/react-dialog';
import { useState } from 'react';

import { Spinner } from '@/components/ui/spinner';
import { fontSizes, radii, shadows, spacing } from '@/theme/tokens';

const ACTION_CONFIG = {
  activate: {
    title: 'Activate Account',
    description: 'This will reactivate the account and restore access.',
    destructive: false,
  },
  cancel: {
    title: 'Cancel Subscription',
    description: 'This will cancel the subscription. The user can resubscribe later.',
    destructive: false,
  },
  disable: {
    title: 'Disable Account',
    description: 'This will disable the account. The user will lose all API access immediately.',
    destructive: true,
  },
} as const;

interface StatusActionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  action: 'activate' | 'cancel' | 'disable';
  userEmail: string;
}

export function StatusActionModal({
  isOpen,
  onClose,
  onConfirm,
  action,
  userEmail,
}: StatusActionModalProps) {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const config = ACTION_CONFIG[action];

  async function handleConfirm() {
    setLoading(true);
    setError(null);
    try {
      await onConfirm();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update status');
    } finally {
      setLoading(false);
    }
  }

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={
        // v8 ignore next
        (open) => {
          if (!open) onClose();
        }
      }
    >
      <Dialog.Portal>
        <Dialog.Overlay
          css={css`
            position: fixed;
            inset: 0;
            background: ${theme.colors.overlay};
            z-index: 50;
          `}
        />
        <Dialog.Content
          css={css`
            position: fixed;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            background: ${theme.colors.surface};
            border: 1px solid ${theme.colors.border};
            border-radius: ${radii.lg};
            box-shadow: ${shadows.prominent};
            padding: ${spacing.xl};
            width: 90vw;
            max-width: 420px;
            z-index: 51;
            display: flex;
            flex-direction: column;
            gap: ${spacing.md};
          `}
        >
          <Dialog.Title
            css={css`
              margin: 0;
              font-size: ${fontSizes.xl};
              color: ${theme.colors.text};
              font-weight: 700;
            `}
          >
            {config.title}
          </Dialog.Title>

          <Dialog.Description
            css={css`
              margin: 0;
              font-size: ${fontSizes.sm};
              color: ${theme.colors.textSecondary};
            `}
          >
            {userEmail}
          </Dialog.Description>

          <p
            css={css`
              margin: 0;
              font-size: ${fontSizes.md};
              color: ${theme.colors.text};
            `}
          >
            {config.description}
          </p>

          {error && (
            <p
              css={css`
                margin: 0;
                color: ${theme.colors.error};
                font-size: ${fontSizes.sm};
              `}
            >
              {error}
            </p>
          )}

          <div
            css={css`
              display: flex;
              gap: ${spacing.sm};
              justify-content: flex-end;
            `}
          >
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              css={css`
                padding: ${spacing.sm} ${spacing.md};
                border: 1px solid ${theme.colors.border};
                border-radius: ${radii.sm};
                background: ${theme.colors.surface};
                color: ${theme.colors.text};
                cursor: pointer;
                font-size: ${fontSizes.sm};
                &:disabled {
                  opacity: 0.5;
                  cursor: not-allowed;
                }
              `}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={loading}
              css={css`
                display: flex;
                align-items: center;
                gap: ${spacing.xs};
                padding: ${spacing.sm} ${spacing.md};
                border: none;
                border-radius: ${radii.sm};
                background: ${config.destructive ? theme.colors.error : theme.colors.primary};
                color: ${config.destructive ? theme.colors.text : theme.colors.primaryText};
                cursor: pointer;
                font-size: ${fontSizes.sm};
                font-weight: 600;
                &:disabled {
                  opacity: 0.5;
                  cursor: not-allowed;
                }
              `}
            >
              {loading && <Spinner size="sm" aria-hidden />}
              {loading ? 'Confirming…' : 'Confirm'}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
